"use client"
import { useState, useEffect, useContext, useRef } from "react"
import {
  Typography,
  Box,
  alpha,
  Tooltip,
  IconButton,
  Checkbox,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  InputAdornment,
  FormControlLabel,
  Chip,
  CircularProgress,
  Collapse,
  useMediaQuery,
} from "@mui/material"
import CalendarTodayIcon from "@mui/icons-material/CalendarToday"
import AttachMoneyIcon from "@mui/icons-material/AttachMoney"
import PersonIcon from "@mui/icons-material/Person"
import PointOfSaleIcon from "@mui/icons-material/PointOfSale"
import LocationOnIcon from "@mui/icons-material/LocationOn"
import SearchIcon from "@mui/icons-material/Search"
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff"
import ReplayIcon from "@mui/icons-material/Replay"
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong"
import VisibilityIcon from "@mui/icons-material/Visibility"
import DeleteIcon from "@mui/icons-material/Delete"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import FilterListIcon from "@mui/icons-material/FilterList"
import TuneIcon from "@mui/icons-material/Tune"
import MenuIcon from "@mui/icons-material/Menu"
import CloseIcon from "@mui/icons-material/Close"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { createTheme, ThemeProvider } from "@mui/material/styles"
import { DataGrid } from "@mui/x-data-grid"
import dayjs from "dayjs"
import Swal from "sweetalert2"

import { ErpContext } from "../../context/ErpContext.js"
import { abrirFacturaPdf } from "../../context/facturaAfipHtmlGenerator.js"
import "./TablaVentasComponent.css"
import SpinnerLading from "../misc/SpinnerComponent.js"
import ModalViewTablaVenta from "./ModalViewTablaVenta.js"
import ModalViewTablaDevolucion from "./ModalViewTablaDevolucion.js"
import CustomPagination from "./CustomPagination.js"

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "rgb(244, 177, 14)",
    },
    background: {
      default: "#f8f9fa",
      paper: "#ffffff",
    },
    text: {
      primary: "#2d3748",
      secondary: "#718096",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 10,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          fontWeight: 500,
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
        containedPrimary: {
          "&:hover": {
            backgroundColor: "#4a4a4e",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            borderRadius: 8,
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&:hover": {
            backgroundColor: alpha("#5b5b5f", 0.08),
          },
        },
      },
    },
  },
})

const FACTURA_ID_TO_CBTE = {
  1: 1,
  2: 6,
  3: 11,
  7: 51,
}

export default function TablaVentas() {
  const {
    getMoneda,
    getTipoTransaccion,
    getUbicacion,
    ubicaciones,
    getNegocio,
    getMedioDePago,
    getSubCategoriaTransaccion,
    getTransaccionItemID,
    generarFacturaPDF,
    getUsuario,
    getTransaccionTransformedVentas,
    getEntidadByID,
    getItemById,
    getItemUbicacionFilter,
    generarRemitoPDF,
    entidadesClientesTresLetras,
    getEntidadesClientesTresLetras,
    getTransaccionMontoTotalByTipoTransaccion,
    updateDeleteTransaccion,
    tiposFacturas,
    getTiposFacturas,
    enviarFacturaAfip,
    getImpuestoItem,
    getIvaAFIP,
    getTransaccionImpuestoItems,
    isNavbarCollapsed,
    getFechaHoraActualClient,
    getParametrosGlobales,
  } = useContext(ErpContext)

  const isLaptopDown = useMediaQuery("(max-width:1200px)")
  const isMobile = useMediaQuery("(max-width:600px)")

  // Estados principales
  const [openModal, setOpenModal] = useState(false)
  const [transaccionItems, setTransaccionItems] = useState([])
  const [selectedTransaccionId, setSelectedTransaccionId] = useState(null)
  const [selectedTransaccion, setSelectedTransaccion] = useState(null)
  const [selectedTransaccionEntidad, setSelectedTransaccionEntidad] = useState(null)
  const [rows, setRows] = useState([])
  const [selectedUbicacion, setSelectedUbicacion] = useState(null)
  const today = dayjs()
  const [startDate, setStartDate] = useState(today.subtract(30, "day"))
  const [endDate, setEndDate] = useState(today)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [totalPages, setTotalPages] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0)
  const [minVentas, setMinVentas] = useState(0)
  const [maxVentas, setMaxVentas] = useState(0)
  const [itemsMap, setItemsMap] = useState({})
  const [loading, setLoading] = useState(true)
  const [sortedData, setSortedData] = useState([])
  const [dniCliente, setDniCliente] = useState("")
  const [searchTextCliente, setSearchTextCliente] = useState("")
  const [filtersActive, setFiltersActive] = useState(false)
  const isFirstRender = useRef(true)
  const [mostrarDeletes, setMostrarDeletes] = useState(false)
  const [idTransaccionSearch, setIdTransaccionSearch] = useState(null)
  const [openModalDevolucion, setOpenModalDevolucion] = useState(false)
  const [idTipoFactura, setIdTipoFactura] = useState(null)
  const [openCuitModal, setOpenCuitModal] = useState(false)
  const [cuitGlobal, setCuitGlobal] = useState("")

  const [facturacionLoading, setFacturacionLoading] = useState(false)
  const [facturarSeleccionados, setFacturarSeleccionados] = useState([])
  const [facturacionDone, setFacturacionDone] = useState(false)
  const [errorList, setErrorList] = useState([])
  const [progressCount, setProgressCount] = useState(0)
  const [progressTotal, setProgressTotal] = useState(0)
  const [buscarCAE, setBuscarCAE] = useState("")
  const [mostrarMasFiltros, setMostrarMasFiltros] = useState(false)
  const [soloFacturadas, setSoloFacturadas] = useState(false)
  const [masFiltrosActive, setMasFiltrosActive] = useState(false)
  const [tipoTransaccionFiltro, setTipoTransaccionFiltro] = useState(1) // 1 = Ventas, 6 = Notas de crédito

  const [filtersOpen, setFiltersOpen] = useState(!isLaptopDown)

  useEffect(() => {
    // Si pasa a laptop/mobile, colapso filtros; en desktop, los muestro siempre
    setFiltersOpen(!isLaptopDown)
  }, [isLaptopDown])

  // Función que alterna el marcado manual
  const toggleFacturar = (id) => {
    setFacturarSeleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  // ✅ Función para calcular IVA detallado por alícuotas para AFIP
  // Ejemplo de resultado:
  // [
  //   { Id: 5, BaseImp: 10000.00, Importe: 2100.00 },    // 21%
  //   { Id: 4, BaseImp:  5000.00, Importe:  525.00 },    // 10,5%
  //   { Id: 3, BaseImp:  2000.00, Importe:     0.00 }    // 0% (si es gravado 0%)
  // ]
  const calcularIvaDetalladoPorAlícuotas = (items) => {
    const ivaAFIP = getIvaAFIP()
    const gruposIva = {}

    // Agrupar items por alícuota de IVA
    items.forEach((item) => {
      let porcentajeIva = 21 // Default
      
      if (item.impuestoData && item.impuestoData.porcentaje !== undefined) {
        porcentajeIva = item.impuestoData.porcentaje
      } else if (item.ivaPercentage !== undefined) {
        porcentajeIva = item.ivaPercentage
      }

      // Encontrar el ID de AFIP correspondiente al porcentaje
      const ivaAFIPEncontrado = ivaAFIP.find((iva) => Math.abs(iva.porcentaje - porcentajeIva) < 0.01)
      const idAFIP = ivaAFIPEncontrado ? ivaAFIPEncontrado.ivaAFIP : 5 // Default a 21%

      if (!gruposIva[idAFIP]) {
        gruposIva[idAFIP] = {
          id: idAFIP,
          porcentaje: porcentajeIva,
          baseImponible: 0,
          importeIva: 0,
          items: [],
        }
      }

      // Calcular valores del item
      const precioItem = item.precio * item.cantidad
      const descuentoItem = (precioItem * (item.porcentajeDescuento || 0)) / 100
      const precioSinDescuento = precioItem - descuentoItem
      
      // Calcular base imponible e IVA
      const baseImponibleItem = precioSinDescuento / (1 + porcentajeIva / 100)
      const ivaItem = precioSinDescuento - baseImponibleItem

      gruposIva[idAFIP].baseImponible += baseImponibleItem
      gruposIva[idAFIP].importeIva += ivaItem
      gruposIva[idAFIP].items.push({
        ...item,
        baseImponibleItem,
        ivaItem,
      })
    })

    // Convertir a array para AFIP
    const ivaArray = Object.values(gruposIva).map((grupo) => ({
      Id: grupo.id,
      BaseImp: Number.parseFloat(grupo.baseImponible.toFixed(2)),
      Importe: Number.parseFloat(grupo.importeIva.toFixed(2)),
    }))

    console.log("IVA detallado por alícuotas:", ivaArray)
    return ivaArray
  }

  // Al pulsar "Facturar":
  const iniciarFacturacion = () => {
    const tipoId = idTipoFactura?.id

    if (!tipoId) {
      Swal.fire({
        title: "Tipo de factura requerido",
        text: "Por favor, selecciona un tipo de factura antes de continuar.",
        icon: "warning",
        confirmButtonText: "Entendido",
        confirmButtonColor: theme.palette.secondary.main,
      })
      return
    }

    const filas = sortedData.filter((r) => facturarSeleccionados.includes(r.id))

    if (filas.length === 0) {
      Swal.fire({
        title: "Sin selección",
        text: "Por favor, selecciona al menos una transacción para facturar.",
        icon: "info",
        confirmButtonText: "Entendido",
        confirmButtonColor: theme.palette.secondary.main,
      })
      return
    }

    // Si el tipo de factura es A y no hay CUIT global, pedimos que lo ingrese
    if (tipoId === 1 && !cuitGlobal && filas.some((f) => !f.dniCuitCuil || f.dniCuitCuil.length !== 11)) {
      setOpenCuitModal(true)
    } else {
      pasarFacturados()
    }
  }

  // Función que envía a AFIP todas las filas marcadas según el tipo de factura seleccionado
  const pasarFacturados = async () => {
    // 0) Mostrar loading y validar que el usuario haya elegido un tipo de factura
    setErrorList([])
    setFacturacionDone(false)
    setFacturacionLoading(true)

    const tipoFacturaId = idTipoFactura?.id
    if (!tipoFacturaId) {
      setFacturacionLoading(false)
      Swal.fire({
        title: "Tipo de factura requerido",
        text: "Selecciona un tipo de factura antes de continuar.",
        icon: "warning",
        confirmButtonColor: theme.palette.secondary.main,
      })
      return
    }

    // 1) Construir la lista de filas que el usuario marcó
    const filasParaFacturar = sortedData.filter((row) => facturarSeleccionados.includes(row.id))
    setProgressTotal(filasParaFacturar.length)
    setProgressCount(0)
    if (filasParaFacturar.length === 0) {
      setFacturacionLoading(false)
      Swal.fire({
        title: "Sin selección",
        text: "Selecciona al menos una transacción para facturar.",
        icon: "info",
        confirmButtonColor: theme.palette.secondary.main,
      })
      return
    }

    try {
      // 2) Recorrer cada fila una a una (secuencialmente)
      for (const fila of filasParaFacturar) {
        setProgressCount((c) => c + 1)
        const idTransaccion = fila.id
        const rawDoc = fila.dniCuitCuil || ""

        // — Validaciones según A/B/C
        if (tipoFacturaId === 1 && rawDoc.length !== 11) {
          setErrorList((el) => [...el, `Transacción ${idTransaccion}: CUIT inválido (11 dígitos)`])
          continue // <— salta al siguiente elemento del for
        }
        if ((tipoFacturaId === 2 || tipoFacturaId === 3) && rawDoc.length !== 8) {
          setErrorList((el) => [...el, `Transacción ${idTransaccion}: DNI inválido (8 dígitos)`])
          continue // <— salta al siguiente elemento del for
        }

        try {
          // 3) Obtener y mapear los ítems al formato que la plantilla EJS espera
          const items = await getTransaccionItemID(idTransaccion)
          
          // ✅ Obtener impuestos específicos para cada item
          const itemsConImpuestos = await Promise.all(
            items.map(async (it) => {
              let impuestoItem = null
              try {
                impuestoItem = await getImpuestoItem(it.idItem)
              } catch (error) {
                console.log("No se encontró impuesto específico para el item:", it.idItem)
                // Si falla, usar el IVA por defecto
                impuestoItem = {
                  idItem: it.idItem,
                  idImpuesto: null,
                  porcentaje: 21,
                  descripcionImpuesto: "IVA General",
                }
              }
              
              return {
                ...it,
                impuestoData: impuestoItem,
              }
            }),
          )
          
          const ItemsList = itemsConImpuestos.map((it) => ({
            codigo: it.idItem,
            descripcion: it.descripcion || "",
            cantidad: it.cantidad || 0,
            unidad: it.unidad || "Unidad",
            precio: it.precio || 0,
            porcentajeDescuento: it.porcentajeDescuento || 0,
          }))

          // 4) ✅ Calcular neto e IVA detallado por alícuotas según el tipo de factura
          const total = fila.montoTotal
          let neto, iva, ivaDetallado
          
          if (tipoFacturaId === 3) {
            // Factura C: no lleva IVA
            neto = total
            iva = 0
            ivaDetallado = []
          } else {
            // Factura A o B: Calcular IVA detallado por alícuotas
            ivaDetallado = calcularIvaDetalladoPorAlícuotas(itemsConImpuestos)
            
            // Calcular totales
            const totalBaseImponible = ivaDetallado.reduce((sum, item) => sum + item.BaseImp, 0)
            const totalIva = ivaDetallado.reduce((sum, item) => sum + item.Importe, 0)
            
            neto = Number.parseFloat(totalBaseImponible.toFixed(2))
            iva = Number.parseFloat(totalIva.toFixed(2))
          }

          // 5) Formatear fecha de comprobante: YYYYMMDD
          const now = new Date()
          const fechaCbte = Number(
            `${now.getFullYear()}${String(now.getMonth() + 1).padStart(
              2,
              "0",
            )}${String(now.getDate()).padStart(2, "0")}`,
          )

          // 6) Determinar DocTipo y DocNro según AFIP para A, B y C
          const parsedDoc = Number(rawDoc) // => 0 o CUIT real
          let docTipoFinal, docNroFinal

          if (tipoFacturaId === 1) {
            // Factura A: siempre CUIT (80). Si falta, usar cuitGlobal del modal.
            docTipoFinal = 80
            docNroFinal = parsedDoc > 0 ? parsedDoc : Number(cuitGlobal)
          } else if (tipoFacturaId === 2) {
            // Factura B: usar CUIT/DNI si existe, sino genérico (99/0).
            if (parsedDoc > 0) {
              docTipoFinal = rawDoc.length === 11 ? 80 : 96
              docNroFinal = parsedDoc
            } else {
              docTipoFinal = 99
              docNroFinal = 0
            }
          } else {
            // Factura C: consumidor final
            docTipoFinal = 99
            docNroFinal = 0
          }

            // 2.1) Condición frente al IVA del receptor (RG 5616)
  // Debes mapear docTipo o tu lógica de cliente a un ID válido:
          let condicionIvaReceptorId
  switch (docTipoFinal) {
    case 99: // Consumidor Final
              condicionIvaReceptorId = 5
              break
    case 96: // Consumidor con DNI
              condicionIvaReceptorId = 5
              break
    case 80: // Responsable Inscripto / CUIT
              condicionIvaReceptorId = 1
              break
    // añade otros casos si manejas Monotributo (6), Exento (7), etc.
    default:
              condicionIvaReceptorId = 5
  }

          // 7) Determinar CbteTipo: 1=A, 6=B, 11=C
          const cbteMap = { 1: 1, 2: 6, 3: 11 }
          const cbteTipo = cbteMap[tipoFacturaId]
          if (!cbteTipo) throw new Error("Tipo de comprobante inválido")

          // 8) Armar payload completo, usando fila.idUbicacion en PtoVta
          const dataParaAfip = {
            PtoVta: fila.idUbicacion, // ahora usamos el id de la fila
            CbteTipo: cbteTipo,
            Concepto: 1,
            DocTipo: docTipoFinal,
            DocNro: docNroFinal,
            CbteDesde: null,
            CbteHasta: null,
            CbteFch: fechaCbte,
            ImpTotal: total,
            ImpTotConc: 0,
            ImpNeto: neto,
            ImpOpEx: 0,
            ImpIVA: iva,
            ImpTrib: 0,
            MonId: "PES",
            MonCotiz: 1,
             // ← Nuevo campo obligatorio RG 5616
          CondicionIVAReceptorId: condicionIvaReceptorId,
            // ✅ Solo para A y B incluimos el detalle de IVA por alícuotas
            ...(cbteTipo !== 11 && {
              Iva: ivaDetallado,
            }),
            ItemsList,
            DniCuitCliente: rawDoc,
            ApellidoNombre: `${fila.descripcionEntidad} ${fila.apellido}`,
            nombreCliente: `${fila.descripcionEntidad}`,
            apellidoCliente: `${fila.apellido}`,
            telefonoCliente: fila.telefono || "",
            emailCliente: fila.email || "",
            direccionCliente: fila.direccion || "",
            localidadCliente: fila.localidad || "",
            provinciaCliente: fila.provincia || "",
            idTipoFacturaTransaccion: tipoFacturaId,
            idTransaccion: idTransaccion,
            abrirPdf: false,
          }

          // 9) Envío a AFIP, usando de nuevo fila.idUbicacion en puntoVenta
          await enviarFacturaAfip({
            ...dataParaAfip,
            subtotal: neto,
            iva,
            puntoVenta: fila.idUbicacion,
          })

          console.log(`✔ Transacción ${idTransaccion} facturada.`)
        } catch (err) {
          // 10) Si falla una transacción, seguimos con la siguiente
          console.error(`❌ Error al facturar ${idTransaccion}:`, err)
          setErrorList((el) => [...el, `Transacción ${idTransaccion}: ${err.message}`])
        }
      }
    } finally {
      // 11) Limpiar selección y resetear estados
      setFacturarSeleccionados([])
      setFacturacionDone(true)
      setFacturacionLoading(false)

      setCuitGlobal("")

      // 12) Refrescar datos
      fetchData(
        startDate,
        page,
        endDate,
        dniCliente,
        selectedUbicacion,
        minVentas,
        maxVentas,
        mostrarDeletes,
        idTransaccionSearch,
        100,
        soloFacturadas,
      )
    }
  }

  // Funciones para manipular fechas
  // const deleteDate1 = () => setStartDate(null)
  // const deleteDate2 = () => setEndDate(null)
  const increaseDate = () => setStartDate((prev) => (prev ? prev.add(1, "day") : dayjs()))
  const decreaseDate = () => setStartDate((prev) => (prev ? prev.subtract(1, "day") : dayjs()))

  // Efectos ***************************************************************************************************************************************************************************************************
  // Efectos ***************************************************************************************************************************************************************************************************
  // Efectos ***************************************************************************************************************************************************************************************************
  // Efectos ***************************************************************************************************************************************************************************************************

  useEffect(() => {
    // console.log("effect inicio")
    if (isFirstRender.current) {
      // console.log("first render if")
      isFirstRender.current = false
      return
    }
    // console.log("first render after if")
    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)
    fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      100,
      soloFacturadas,
      tipoTransaccionFiltro,
    )
    fetchDataInicio()
    setLoading(false)
  }, [])

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    // console.log("efecto startdate")
    fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      100,
      soloFacturadas,
      tipoTransaccionFiltro,
    )
    setLoading(false)
  }, [startDate])

  useEffect(() => {
    // console.log("effect page")
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    // setLoading(true)
    fetchData(
      startDate,
      page,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      100,
      soloFacturadas,
      tipoTransaccionFiltro,
    )
    setLoading(false)
  }, [page])

  // Verificar si hay filtros activos
  useEffect(() => {
    // console.log("effect varios")
    const hasActiveFilters =
      (endDate && startDate && !endDate.isSame(startDate, "day")) ||
      dniCliente ||
      selectedUbicacion ||
      minVentas > 0 ||
      maxVentas > 0 ||
      idTransaccionSearch ||
      buscarCAE ||
      soloFacturadas ||
      tipoTransaccionFiltro !== 1 // Considerar activo si no es el default (ventas)

    setFiltersActive(hasActiveFilters)
    setLoading(false)
  }, [
    startDate,
    endDate,
    dniCliente,
    selectedUbicacion,
    minVentas,
    maxVentas,
    idTransaccionSearch,
    buscarCAE,
    soloFacturadas,
    tipoTransaccionFiltro,
  ])

  // Funciones para manejar filtros
  const buttonFiltrado = () => {
    console.log("Frontend - buttonFiltrado - soloFacturadas:", soloFacturadas, "type:", typeof soloFacturadas)
    fetchData(
      startDate,
      1,
      endDate,
      dniCliente,
      selectedUbicacion,
      minVentas,
      maxVentas,
      mostrarDeletes,
      idTransaccionSearch,
      100,
      soloFacturadas,
      tipoTransaccionFiltro,
    ) // cuando le doy al boton buscar tengo que ir a la pagina 1.
  }

  const fetchDataInicio = async () => {
    try {
      await getUbicacion()
      await getTiposFacturas()
    } catch (error) {
      console.error("Error al cargar ubicaciones:", error)
    }
  }

  const handleMinChange = (e) => {
    const newMin = Number(e.target.value)
    if (newMin >= 0 && newMin <= maxVentas) {
      setMinVentas(newMin)
    } else if (newMin < 0) {
      setMinVentas(0)
    }
  }

  const handleMaxChange = (e) => {
    const newMax = Number(e.target.value)
    if (newMax >= 0 && newMax >= minVentas) {
      setMaxVentas(newMax)
    } else if (newMax < 0) {
      setMaxVentas(0)
    }
  }

  const fetchData = async (
    date,
    page,
    endDate,
    dniCliente,
    selectedUbicacion,
    valorMin,
    valorMax,
    mostrarDeletes,
    idTransaccionSearch,
    limit = 100,
    soloFacturadas = false,
    tipoTransaccionFiltro = 1,
  ) => {
    const controller = new AbortController()
    const signal = controller.signal
    try {
      if (isFirstRender.current) {
        isFirstRender.current = false
        return
      }

      setLoading(true)

      const validDate = date ? date : dayjs()
      let formattedDate = validDate.format("YYYY-MM-DD")
      // const formattedDate = await date.format("YYYY-MM-DD")
      let formattedDate2 = endDate
      let idDni = null
      let idSelectedUbicacion = null

      if (endDate && typeof endDate.format === "function") {
        formattedDate2 = endDate.format("YYYY-MM-DD")
      }
      if (dniCliente != null) {
        idDni = dniCliente.id
      }
      if (selectedUbicacion != null) {
        idSelectedUbicacion = selectedUbicacion.id
      }

      const result1 = await getFechaHoraActualClient(formattedDate) // pedimos la fecha del servidor
      formattedDate = dayjs(result1.fechaServidor).startOf("day").toDate() // formateamos la fecha

      const result2 = await getFechaHoraActualClient(formattedDate2) // pedimos la fecha del servidor
      formattedDate2 = dayjs(result2.fechaServidor).endOf("day").toDate() // formateamos la fecha

      const data = await getTransaccionTransformedVentas(
        tipoTransaccionFiltro,
        formattedDate,
        page,
        limit,
        { signal },
        formattedDate2,
        idDni,
        idSelectedUbicacion,
        valorMin,
        valorMax,
        mostrarDeletes,
        idTransaccionSearch,
        "", //Para no traer devoluciones paso el char vacio
        buscarCAE,
        null, //tipoTransaccionFiltro
        null, //canalEntidad
        soloFacturadas,
      )
      const transaccionTransformedVentas = data.data

      const transaccionTransformedReforged = transaccionTransformedVentas.map((transaccion) => {
          const pagos = transaccion.pagos.map((pago) => ({
            descripcion: pago.medioDePago,
            monto: pago.montoTotal,
        }))

          const formattedDate = new Intl.DateTimeFormat("es-AR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        }).format(new Date(transaccion.fechaHoraCreacion))

        console.log("transaccion", transaccion)

          return {
            id: transaccion.id,
            createdAt: formattedDate,
            montoTotal: transaccion.montoTotal,
            idMoneda: transaccion.pagos?.[0]?.cotizacion || "",
            idTipoTransaccion: transaccion.tipoTransaccion?.descripcion || "",
            CAE: transaccion.transaccionTipoFactura?.CAE || "",
            vencimientoCAE: transaccion.transaccionTipoFactura?.vencimientoCAE || "",
            pagos: pagos,
            descripcionEntidad: transaccion.entidad?.descripcion || "",
            dniCuitCuil: transaccion.entidad?.dniCuitCuil || "",
            apellido: transaccion.entidad?.apellido || "",
            direccion: transaccion.entidad?.direccion || "",
            localidad: transaccion.entidad?.localidad || "",
            provincia: transaccion.entidad?.provincia || "",
            telefono: transaccion.entidad?.telefono || "",
            email: transaccion.entidad?.email || "",
            usuario: transaccion.usuario?.usuario || "",
            negocio: transaccion.negocio?.descripcion || "",
            ubicacion: transaccion.ubicacion?.descripcion || "",
            idEntidadNumero: transaccion.idEntidad,
            fechaHoraCreacion: transaccion.fechaHoraCreacion,
            impuestos: transaccion.impuestos,
            montoDescuento: transaccion.montoDescuento,
            tipoFacturaDescripcion: transaccion.tipoFacturaDescripcion,
            idTipoFacturaTransaccion: transaccion.idTipoFacturaTransaccion,
            porcentajeDescuento: transaccion.porcentajeDescuento,
            tipoTransaccion: transaccion.tipoTransaccion,
            idTipoTransaccionID: transaccion.idTipoTransaccion,
            idUbicacion: transaccion.ubicacion?.id || "",
        }
      })

      setPage(data.currentPage)
      setTotalPages(data.totalPages)
      setTotalCount(data.total)
      setMontoTotalTransacciones(data.totalMontoVentas)

      setSortedData(transaccionTransformedReforged)
    } catch (error) {
      console.error("Error al cargar los datos:", error)
    } finally {
      // console.log("finally")
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value)
  }

  useEffect(() => {
    if (searchTextCliente.length >= 3) {
      getEntidadesClientesTresLetras(searchTextCliente, 1)
      setLoading(false)
    }
  }, [searchTextCliente])

  useEffect(() => {
    // console.log("effect ti gi")
    const fetchItemsDetails = async () => {
      const itemsDetails = {}

      await Promise.all(
        transaccionItems.map(async (transaccionItem) => {
          const foundItem = await getItemById(transaccionItem.idItem)
          if (foundItem) {
            itemsDetails[transaccionItem.idItem] = foundItem
          }
        }),
      )

      setItemsMap(itemsDetails)
    }

    fetchItemsDetails()
    setLoading(false)
  }, [transaccionItems, getItemById])

  const columns = [
    {
      field: "id",
      headerName: "ID",
      flex: 0.3,
      minWidth: 70,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "createdAt",
      headerName: "Fecha",
      flex: 1,
      headerAlign: "center",
      minWidth: 200,
      renderCell: (params) => (
        <Box sx={{ display: "flex", justifyContent: "flex-start", gap: 1 }}>
          <CalendarTodayIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "montoTotal",
      headerName: "Monto",
      type: "number",
      flex: 0.8,
      minWidth: 130,
      headerAlign: "center",
      renderCell: (params) => {
        const esDevolucion = params.row?.idTipoTransaccionID === 6
        return (
          <Chip
            icon={<AttachMoneyIcon fontSize="small" />}
            label={formatCurrency(params.value)}
            size="small"
            sx={{
              fontWeight: "bold",
              bgcolor: esDevolucion ? alpha("#9e9e9e", 0.1) : alpha("#4CAF50", 0.1),
              color: esDevolucion ? "#757575" : "#4CAF50",
              "& .MuiChip-icon": {
                color: esDevolucion ? "#757575" : "#4CAF50",
              },
              display: "flex",
            }}
          />
        )
      },
    },
    {
      field: "descripcionEntidad",
      headerName: "Cliente",
      flex: 1.2,
      headerAlign: "center",
      minWidth: 150,
      renderCell: (params) => (
        <Tooltip title={`${params.row.descripcionEntidad} ${params.row.apellido || ""}`}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PersonIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
            <Typography variant="body2" noWrap fontWeight={"500"}>
              {params.row.descripcionEntidad} {params.row.apellido || ""}
            </Typography>
          </Box>
        </Tooltip>
      ),
    },
    {
      field: "pagos",
      headerName: "Medio de Pago",
      flex: 1,
      minWidth: 200,
      headerAlign: "center",
      renderCell: (params) => {
        const pagos = Array.isArray(params.row.pagos) ? params.row.pagos : []

        if (pagos.length > 1) {
          return <Chip label="Múltiple" size="small" color="info" sx={{ fontWeight: "bold", display: "flex" }} />
        }

        return (
          <Chip
            icon={<PointOfSaleIcon fontSize="small" />}
            label={pagos[0]?.descripcion || "N/A"}
            size="small"
            variant="outlined"
            sx={{
              fontWeight: "bold",
              color: "#00AAE4",
              borderColor: alpha("#00AAE4", 0.5),
              "& .MuiChip-icon": { color: "#00AAE4" },
              display: "flex",
            }}
          />
        )
      },
    },

    {
      field: "usuario",
      headerName: "Vendedor",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontStyle: "italic" }}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "ubicacion",
      headerName: "Ubicación",
      flex: 1,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <LocationOnIcon fontSize="small" sx={{ color: alpha("#5b5b5f", 0.7) }} />
          <Typography variant="body2">{params.value}</Typography>
        </Box>
      ),
    },
    {
      field: "CAE",
      headerName: "CAE",
      flex: 0.8,
      minWidth: 120,
      headerAlign: "center",
    },
    {
      field: "tipoFacturaDescripcion",
      headerName: "Tipo de Factura",
      flex: 0.5,
      minWidth: 100,
      headerAlign: "center",
    },
    // Solo mostrar columna "Facturar" para ventas (tipoTransaccionFiltro === 1)
    ...(tipoTransaccionFiltro === 1 ? [{
      field: "facturar",
      headerName: "Facturar",
      flex: 0.5,
      minWidth: 100,
      headerAlign: "center",
      renderCell: (params) => {
        const { id, tipoFacturaDescripcion } = params.row
        // si ya viene A/B/C, siempre checked y deshabilitado
        const isAutoChecked = ["A", "B", "C"].includes(tipoFacturaDescripcion)
        // si no, chequeado sólo si está en nuestro estado
        const isChecked = isAutoChecked || facturarSeleccionados.includes(id)

        return (
          <Checkbox
            checked={isChecked}
            disabled={isAutoChecked}
            onChange={() => toggleFacturar(id)}
            // aplica color distinto a los que el usuario marcó manualmente
            sx={{
              ...(isAutoChecked
                ? {} // estilo por defecto
                : isChecked
                ? {
                    // ej. los marcados manualmente en azul
                    color: (theme) => theme.palette.primary.main,
                  }
                : {}),
            }}
          />
        )
      },
    }] : []),
    {
      field: "action",
      headerName: "Acciones",
      flex: 0.8,
      minWidth: 150,
      headerAlign: "center",
      renderCell: (params) => {
        const { id, tipoFacturaDescripcion, CAE } = params.row

        return (
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Ver detalles">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => handleOpenModal(params.row)}
                size="small"
                sx={{
                  bgcolor: alpha("#5b5b5f", 0.1),
                  "&:hover": { bgcolor: alpha("#5b5b5f", 0.2) },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {CAE && (
          <Tooltip title="Descargar Factura Afip PDF">
            <IconButton
              onClick={() => handleDownloadPDF(params.row)}
              size="small"
              sx={{
                bgcolor: alpha("#4CAF50", 0.1),
                "&:hover": { bgcolor: alpha("#4CAF50", 0.2) },
              }}
            >
              <PictureAsPdfIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}

            <Tooltip title="Borrar Transaccion">
              <IconButton
                className="colorGeneralParaIconos"
                onClick={() => borrarTransaccion(params.row)}
                size="small"
                sx={{
                  bgcolor: alpha("rgb(244, 177, 14)", 0.1),
                  "&:hover": { bgcolor: alpha("rgb(244, 177, 14)", 0.2) },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      },
    },
  ]

  const resetFilters = () => {
    setDniCliente(null)
    setSelectedUbicacion(null)
    setSearchTextCliente("")
    setMinVentas(0)
    setMaxVentas(0)
    setMostrarDeletes(false)
    setIdTransaccionSearch("")
    setBuscarCAE("")
    setSoloFacturadas(false)
    setMostrarMasFiltros(false)
    setTipoTransaccionFiltro(1) // Reset a ventas

    const today = dayjs()
    setStartDate(today.subtract(30, "day"))
    setEndDate(today)

    setMasFiltrosActive(false)
    fetchData(startDate, 1, endDate, null, null, 0, 0, false, null, 100, false, 1)
  }

  const mostrarFiltros = () => setMasFiltrosActive(!masFiltrosActive)

  const borrarTransaccion = async (transaccionRow) => {
    const result = await Swal.fire({
      title: "¿Estás seguro?",
      text: `Vas a eliminar la transacción con ID ${transaccionRow.id}. Esta acción no se puede deshacer.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, eliminar",
      cancelButtonText: "Cancelar",
    })

    if (result.isConfirmed) {
      try {
        await updateDeleteTransaccion(transaccionRow.id)
        await fetchData(
          startDate,
          page,
          endDate,
          dniCliente,
          selectedUbicacion,
          minVentas,
          maxVentas,
          mostrarDeletes,
          idTransaccionSearch,
          100,
          soloFacturadas,
        )
        Swal.fire({
          title: "Eliminado",
          text: `La transacción ${transaccionRow.id} ha sido eliminada.`,
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
      } catch (error) {
        console.error("Error al borrar transacción:", error)
        Swal.fire({
          title: "Error",
          text: "No se pudo eliminar la transacción. Intenta de nuevo.",
          icon: "error",
        })
      }
    }
}

  const handleDownloadPDF = async (transaccionRow) => {
    try {
      setLoading(true)
      const transaccionEncontrada = await getTransaccionItemID(transaccionRow.id)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)

      if (!transaccionEncontrada || transaccionEncontrada.length === 0) {
        throw new Error("No se encontraron ítems para la transacción seleccionada")
      }

      const itemsConDetalles = await Promise.all(
        transaccionEncontrada.map(async (transItem) => {
          const [foundItem, impuestoItem] = await Promise.all([
            getItemById(transItem.idItem),
            getImpuestoItem(transItem.idItem).catch(() => null),
          ])

          const porcentajeIva =
            impuestoItem?.porcentaje ??
            impuestoItem?.porcentajeImpuesto ??
            transItem.porcentajeIva ??
            21

          return {
            codigo: foundItem?.codigo || transItem.idItem,
            descripcion: foundItem?.descripcion || "Descripción no disponible",
            cantidad: Number(transItem.cantidad || 0),
            precio: Number(transItem.precio || 0),
            porcentajeDescuento: Number(transItem.porcentajeDescuento || 0),
            unidad: foundItem?.unidadMedida || foundItem?.unidad || "Unidad",
            impuestoData: impuestoItem
              ? { ...impuestoItem, porcentaje: porcentajeIva }
              : { porcentaje: porcentajeIva },
            ivaPercentage: porcentajeIva,
          }
        }),
      )

      const puntoVenta = Number(transaccionRow.idUbicacion) || 1
      const tipoFacturaId = Number(transaccionRow.idTipoFacturaTransaccion) || 2
      const cbteTipo = FACTURA_ID_TO_CBTE[tipoFacturaId] || 6
      const fechaCbte = dayjs(transaccionRow.fechaHoraCreacion).format("YYYYMMDD")
      const numeroFactura =
        transaccionRow.numeroFactura ||
        transaccionRow.cbteNro ||
        transaccionRow.transaccionTipoFactura?.numeroFactura ||
        transaccionRow.facturaOriginal?.numeroFactura ||
        ""
      const cbteNumero = numeroFactura
        ? Number.parseInt(String(numeroFactura).replace(/\D/g, ""), 10) || undefined
        : undefined
      const vencimientoCae = transaccionRow.vencimientoCAE
        ? dayjs(transaccionRow.vencimientoCAE).format("YYYYMMDD")
        : undefined

      const docCliente =
        entidadCliente?.dniCuitCuil ||
        transaccionRow.dniCuitCuil ||
        transaccionRow.DniCuitCliente ||
        ""

      const condicionVenta = transaccionRow.pagos?.length
        ? transaccionRow.pagos.map((pago) => pago.descripcion).join(" / ")
        : "CONTADO"

      const payloadDesdeDb = {
        idTransaccion: transaccionRow.id,
        idTipoFacturaTransaccion: tipoFacturaId,
        puntoVenta,
        PtoVta: puntoVenta,
        CbteFch: fechaCbte,
        CbteDesde: cbteNumero,
        CbteHasta: cbteNumero,
        numeroFactura: numeroFactura || undefined,
        ItemsList: itemsConDetalles,
        DniCuitCliente: docCliente,
        ApellidoNombre: `${entidadCliente?.descripcion || transaccionRow.descripcionEntidad || ""} ${
          entidadCliente?.apellido || transaccionRow.apellido || ""
        }`.trim(),
        nombreCliente: entidadCliente?.descripcion || transaccionRow.descripcionEntidad || "",
        apellidoCliente: entidadCliente?.apellido || transaccionRow.apellido || "",
        condicionVenta,
        condicionIvaCliente:
          entidadCliente?.condicionIvaDescripcion ||
          entidadCliente?.condicionIVA ||
          transaccionRow.condicionIvaCliente ||
          entidadCliente?.condicionIva ||
          null,
        direccionCliente: entidadCliente?.direccion || transaccionRow.direccion || "",
        localidadCliente: entidadCliente?.localidad || transaccionRow.localidad || "",
        provinciaCliente: entidadCliente?.provincia || transaccionRow.provincia || "",
        telefonoCliente: entidadCliente?.telefono || transaccionRow.telefono || "",
        emailCliente: entidadCliente?.email || transaccionRow.email || "",
        porcentajeDescuento: Number(transaccionRow.porcentajeDescuento || 0),
        montoDescuento: Number(transaccionRow.montoDescuento || 0),
        periodoDesde: fechaCbte,
        periodoHasta: fechaCbte,
      }

      const dataDesdeDb = {
        CbteTipo: cbteTipo,
        cbteTipo,
        CbteNro: cbteNumero,
        cbteNro: cbteNumero,
        CbteDesde: cbteNumero,
        CbteHasta: cbteNumero,
        CbteFch: fechaCbte,
        cbteFch: fechaCbte,
        CAE: transaccionRow.CAE || transaccionRow.cae,
        cae: transaccionRow.CAE || transaccionRow.cae,
        Vto: vencimientoCae,
        vto: vencimientoCae,
        PtoVta: puntoVenta,
        ptoVta: puntoVenta,
      }

      await abrirFacturaPdf(dataDesdeDb, payloadDesdeDb, getParametrosGlobales)
    } catch (error) {
      console.error("Error al generar PDF:", error)
      Swal.fire({
        title: "Error",
        text: "No se pudo generar el PDF con la información almacenada.",
        icon: "error",
      })
    } finally {
      setLoading(false)
    }
  }

  const [calculatedTotal, setCalculatedTotal] = useState(null)
  const [totalMontoDescuento, setTotalMontoDescuento] = useState(null)
  const [totalPorcentajeDescuento, setPorcentajeDescuento] = useState(null)
  const [montoSinImpuesto, setMontoSinImpuesto] = useState(null)

  const handleOpenModal = async (transaccionRow) => {
    try {
      setLoading(true)
      setSelectedTransaccion(transaccionRow)
      const entidadCliente = await getEntidadByID(transaccionRow.idEntidadNumero)
      setSelectedTransaccionEntidad(entidadCliente)

      const items = await getTransaccionItemID(transaccionRow.id)
      setTransaccionItems(items)

      const { montoTotal, impuestos = [], montoDescuento = 0, porcentajeDescuento = 0 } = transaccionRow
      const totalSinImpuestos = impuestos.reduce((total, impuesto) => total - (impuesto.montoTotal || 0), montoTotal)
      const totalSinImpuestosYDescuento = totalSinImpuestos + montoDescuento
      const totalFinal = totalSinImpuestosYDescuento * (1 - porcentajeDescuento / 100)

      setCalculatedTotal(totalFinal)
      setTotalMontoDescuento(montoDescuento)
      setPorcentajeDescuento(porcentajeDescuento)
      setMontoSinImpuesto(totalSinImpuestos)

      switch (transaccionRow.idTipoTransaccionID) {
        case 1: // venta
          setOpenModal(true)
          break
        case 6: // devolucion
          setOpenModalDevolucion(true)
          break
      }
    } catch (error) {
      console.error("Error al abrir modal:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setOpenModalDevolucion(false)
  }

  // Componente LoadingOverlay
  const LoadingOverlay = ({ facturacionLoading, done, errors, progressCount, progressTotal, onAccept }) => (
    <Dialog
      open={facturacionLoading || done}
      PaperProps={{
        sx: {
          borderRadius: "12px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          width: "500px",
          maxWidth: "90vw",
        },
      }}
    >
      <DialogTitle
          sx={{
          borderBottom: "1px solid #eaeaea",
          padding: "20px 24px",
          fontSize: "1.2rem",
          fontWeight: 600,
        }}
      >
        {facturacionLoading ? "Procesando Facturación..." : "Facturación Completada"}
      </DialogTitle>
      <DialogContent sx={{ padding: "24px" }}>
        {facturacionLoading && (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <CircularProgress />
            <Typography>
              Procesando {progressCount} de {progressTotal} transacciones...
              </Typography>
          </Box>
        )}
        {done && (
          <Box>
            <Typography variant="h6" sx={{ mb: 2, color: "success.main" }}>
              <CheckCircleOutlineIcon sx={{ mr: 1, verticalAlign: "middle" }} />
              Proceso completado
            </Typography>
          {errors.length > 0 && (
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1, color: "error.main" }}>
                  Errores encontrados:
                </Typography>
                <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                  {errors.map((error, index) => (
                    <Typography key={index} variant="body2" sx={{ color: "error.main", mb: 0.5 }}>
                      • {error}
                  </Typography>
                ))}
              </Box>
              </Box>
          )}
          </Box>
        )}
      </DialogContent>
          {done && (
        <DialogActions sx={{ padding: "16px 24px", borderTop: "1px solid #eaeaea" }}>
            <Button
              onClick={onAccept}
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              bgcolor: theme.palette.secondary.main,
              "&:hover": {
                bgcolor: alpha(theme.palette.secondary.main, 0.9),
              },
            }}
            >
              Aceptar
            </Button>
        </DialogActions>
      )}
    </Dialog>
  )

  if (loading) return <SpinnerLading />

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100%",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#f8f9fa",
      }}
    >
      <ThemeProvider theme={theme}>
        {/* Header con botón de filtros para móvil */}
        {isLaptopDown && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1.5,
              backgroundColor: "white",
              borderBottom: "1px solid #e0e0e0",
              boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
            }}
          >
            <Button
              variant="outlined"
              size="small"
              startIcon={<FilterListIcon />}
              onClick={() => setFiltersOpen(!filtersOpen)}
              sx={{ 
                textTransform: "none",
                borderRadius: 2,
                px: 2,
              }}
            >
              {filtersOpen ? "Ocultar filtros" : "Mostrar filtros"}
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={buttonFiltrado}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: "#5b5b5f",
                textTransform: "none",
                borderRadius: 2,
                px: 2,
                "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
              }}
            >
              Buscar
            </Button>
          </Box>
        )}

        {/* Contenedor principal con scroll */}
            <Box
              sx={{
            flex: 1,
            overflow: "auto",
                display: "flex",
                flexDirection: "column",
          }}
        >
          {/* Filtros principales */}
          <Collapse in={filtersOpen || !isLaptopDown} timeout={300}>
            <Paper
              elevation={1}
              sx={{
                m: 1.5,
                mb: 1,
                borderRadius: 3,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: "1px solid #e0e0e0",
              }}
            >
              <Box sx={{ p: 2 }}>
                {/* Fila única con todos los filtros */}
              <Box
                sx={{
                  display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                  alignItems: "center",
                    justifyContent: "space-between",
                }}
              >
                  {/* Filtros de fecha */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 320 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <IconButton
                    onClick={decreaseDate}
                    color="primary"
                    size="small"
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                        }}
                  >
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <DatePicker
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format="DD/MM/YYYY"
                    label="Desde"
                    slotProps={{
                      textField: {
                        size: "small",
                            sx: { width: 140 }
                          } 
                    }}
                  />
                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    format="DD/MM/YYYY"
                    label="Hasta"
                    slotProps={{
                      textField: {
                        size: "small",
                            sx: { width: 140 }
                          } 
                    }}
                  />
                  <IconButton
                    onClick={increaseDate}
                    color="primary"
                    size="small"
                        sx={{ 
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                        }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </IconButton>
                </LocalizationProvider>
              </Box>

                   {/* Búsquedas */}
                   <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 320 }}>
                 <TextField
                   label="Buscar por ID"
                   type="number"
                   size="small"
                   value={idTransaccionSearch}
                   onChange={(e) => setIdTransaccionSearch(e.target.value)}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                             <SearchIcon fontSize="small" />
                       </InputAdornment>
                     ),
                   }}
                       sx={{ width: 150 }}
                 />
                   <TextField
                   label="Buscar por CAE"
                   type="number"
                   size="small"
                   value={buscarCAE}
                   onChange={(e) => setBuscarCAE(e.target.value)}
                   InputProps={{
                     startAdornment: (
                       <InputAdornment position="start">
                             <SearchIcon fontSize="small" />
                       </InputAdornment>
                     ),
                   }}
                       sx={{ width: 150 }}
                 />
                 </Box>

                   {/* Tipo de transacción */}
                   <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 200 }}>
                     <Autocomplete
                       size="small"
                       disablePortal
                       options={[
                         { id: 1, descripcion: "Facturas Ventas" },
                         { id: 6, descripcion: "Notas de Crédito" }
                       ]}
                       getOptionLabel={(option) => option.descripcion || ""}
                       value={{ id: tipoTransaccionFiltro, descripcion: tipoTransaccionFiltro === 1 ? "Facturas Ventas" : "Notas de Crédito" }}
                       onChange={(event, newValue) => setTipoTransaccionFiltro(newValue?.id || 1)}
                       sx={{ minWidth: 180 }}
                       renderInput={(params) => (
                         <TextField {...params} label="Tipo de transacción" />
                       )}
                     />
                   </Box>

                  {/* Facturación - Solo visible para ventas */}
                  {tipoTransaccionFiltro === 1 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 350 }}>
                      <Autocomplete
                        size="small"
                        disablePortal
                        options={tiposFacturas}
                        getOptionLabel={(option) => option.descripcion || ""}
                        value={idTipoFactura}
                        onChange={(event, newValue) => setIdTipoFactura(newValue)}
                        sx={{ minWidth: 200 }}
                        renderInput={(params) => (
                          <TextField {...params} label="Tipo de factura" />
                        )}
                      />
                      <Button
                        onClick={iniciarFacturacion}
                        variant="contained"
                        startIcon={<ReceiptLongIcon />}
                        sx={{
                          backgroundColor: theme.palette.secondary.main,
                          color: "white",
                          textTransform: "none",
                          fontWeight: 500,
                          borderRadius: 2,
                          px: 2,
                          "&:hover": {
                            backgroundColor: alpha(theme.palette.secondary.main, 0.9),
                          },
                        }}
                      >
                        Facturar seleccionados
                      </Button>
                    </Box>
                  )}

            {/* Botones de acción */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                onClick={buttonFiltrado}
                startIcon={<SearchIcon />}
                sx={{
                  backgroundColor: "#5b5b5f",
                  textTransform: "none",
                        borderRadius: 2,
                        px: 2,
                        "&:hover": { backgroundColor: alpha("rgb(244, 177, 14)", 0.8) },
                }}
              >
                Buscar
              </Button>

              <Tooltip title={filtersActive ? "Resetear filtros" : "Refrescar"}>
                <IconButton
                  onClick={resetFilters}
                  color="primary"
                  size="small"
                  sx={{
                          bgcolor: filtersActive ? alpha(theme.palette.secondary.main, 0.1) : "transparent",
                          "&:hover": { bgcolor: alpha(theme.palette.secondary.main, 0.2) },
                        }}
                      >
                        {filtersActive ? <FilterAltOffIcon /> : <ReplayIcon />}
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={mostrarMasFiltros ? "Ocultar filtros" : "Mostrar más filtros"}>
                      <IconButton
                        onClick={() => setMostrarMasFiltros(!mostrarMasFiltros)}
                        color="primary"
                        size="small"
                        sx={{
                          bgcolor: mostrarMasFiltros
                            ? alpha(theme.palette.primary.main, 0.1)
                      : "transparent",
                    "&:hover": {
                            bgcolor: alpha(theme.palette.primary.main, 0.2),
                    },
                  }}
                >
                        {mostrarMasFiltros ? <CloseIcon /> : <MenuIcon />}
                </IconButton>
              </Tooltip>
            </Box>
                </Box>
              </Box>
            </Paper>
          </Collapse>

          {/* Sección de filtros adicionales expandible */}
          <Collapse in={mostrarMasFiltros} timeout={300}>
            <Paper
              elevation={2}
              sx={{
                m: 1.5,
                mb: 1,
                borderRadius: 3,
                backgroundColor: "white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
            <Box
              sx={{
                  p: 2,
                display: "flex",
                flexDirection: "column",
                  gap: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                gap: 1,
                  }}
                >
                  <MenuIcon fontSize="small" />
                  Filtros Adicionales
                </Typography>
                
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 2,
                    alignItems: "center",
                  }}
                >
                  {/* Filtros de monto */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <TextField
                      label="Monto mínimo"
                      type="number"
                      size="small"
                      value={minVentas}
                      onChange={handleMinChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment> 
                      }}
                      sx={{ width: 140 }}
                    />
                    <TextField
                      label="Monto máximo"
                      type="number"
                      size="small"
                      value={maxVentas}
                      onChange={handleMaxChange}
                      InputProps={{ 
                        startAdornment: <InputAdornment position="start">$</InputAdornment> 
                      }}
                      sx={{ width: 140 }}
                    />
                  </Box>

                  {/* Filtros de cliente y ubicación */}
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 300 }}>
              <Autocomplete
                size="small"
                disablePortal
                      options={entidadesClientesTresLetras.map((it) => ({
                        label: `${it.descripcion} - ${it.apellido} - ${it.dniCuitCuil}`,
                        ...it,
                      }))}
                      value={dniCliente}
                      onChange={(event, newValue) => setDniCliente(newValue || null)}
                      onInputChange={(event, newInputValue) => setSearchTextCliente(newInputValue)}
                      sx={{ flex: 1 }}
                renderInput={(params) => (
                        <TextField {...params} label="Cliente" variant="outlined" />
                      )}
                    />
                    <Autocomplete
                      size="small"
                      disablePortal
                      options={ubicaciones.map((it) => ({
                        id: it.id,
                        label: it.descripcion,
                      }))}
                      value={selectedUbicacion}
                      onChange={(event, newValue) => setSelectedUbicacion(newValue)}
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} label="Ubicación" variant="outlined" />
                      )}
                    />
                  </Box>

                  {/* Filtro de facturación */}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={soloFacturadas}
                        onChange={(e) => {
                          console.log("Checkbox onChange - checked:", e.target.checked)
                          setSoloFacturadas(e.target.checked)
                        }}
                        size="small"
                sx={{
                          color: theme.palette.primary.main,
                          "&.Mui-checked": {
                            color: theme.palette.primary.main,
                          },
                        }}
                      />
                    }
                    label="Solo ventas facturadas"
                    sx={{ 
                      fontSize: "0.875rem",
                      fontWeight: 500,
                    }}
                  />
                  
            </Box>
          </Box>
        </Paper>
          </Collapse>

          {/* Tabla de datos */}
        <Paper
            elevation={1}
          sx={{
            flex: 1,
              m: 1.5,
              mt: 0,
            borderRadius: 3,
              backgroundColor: "white",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              border: "1px solid #e0e0e0",
              display: "flex",
              flexDirection: "column",
              minHeight: 0, // Importante para que funcione el flex
          }}
        >
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            className="tablaVentasDataGrid"
            rows={sortedData}
            columns={columns}
            getRowId={(row) => row.id}
            rowCount={totalPages * 100}
            pagination
            paginationMode="server"
              getRowHeight={() => 40}
              headerHeight={45}
              getRowClassName={(params) => (params.row?.idTipoTransaccionID === 6 ? "row-devolucion" : "")}
            sx={{
                flex: 1,
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
                  fontWeight: 600,
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                  fontSize: "0.875rem",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                    backgroundColor: "#fafafa",
                },
                "&:hover": {
                    backgroundColor: "#f0f8ff !important",
                    transition: "background 0.2s ease",
                },
              },
              "& .row-devolucion": {
                  backgroundColor: alpha("#9e9e9e", 0.05),
                "&:hover": {
                    backgroundColor: alpha("#9e9e9e", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                  "&::-webkit-scrollbar": {
                    width: "6px",
                    height: "6px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "3px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#c1c1c1",
                    borderRadius: "3px",
                    "&:hover": {
                      backgroundColor: "#a8a8a8",
                    },
                  },
              },
            }}
            components={{
              Pagination: () => null,
            }}
          />
        </Paper>

          {/* Paginación */}
          <Box 
            sx={{ 
              flexShrink: 0, 
              p: 1.5,
              backgroundColor: "white",
              borderTop: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "center",
            }}
          >
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"tablaVentas"}
            onPageChange={(newPage) => setPage(newPage)}
          />
          </Box>
        </Box>

        {openModal === true ? (
          <ModalViewTablaVenta
            openModal={openModal}
            handleCloseModal={handleCloseModal}
            selectedTransaccion={selectedTransaccion}
          />
        ) : null}

        <Dialog
          open={openCuitModal}
          onClose={() => setOpenCuitModal(false)}
          PaperProps={{
            sx: {
              borderRadius: "12px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
              width: "450px",
              maxWidth: "90vw",
            },
          }}
        >
          <DialogTitle
            sx={{
              borderBottom: "1px solid #eaeaea",
              padding: "20px 24px",
              fontSize: "1.2rem",
              fontWeight: 600,
            }}
          >
            Ingresar CUIT para Factura A
          </DialogTitle>
          <DialogContent sx={{ padding: "24px" }}>
            <TextField
              label="CUIT (11 dígitos)"
              value={cuitGlobal}
              onChange={(e) => setCuitGlobal(e.target.value)}
              fullWidth
              variant="outlined"
              InputProps={{
                sx: { borderRadius: "8px" },
              }}
              helperText="Obligatorio para Factura A cuando cliente genérico"
              sx={{ mt: 1 }}
            />
          </DialogContent>
          <DialogActions
            sx={{
              padding: "16px 24px",
              borderTop: "1px solid #eaeaea",
              gap: 1,
            }}
          >
            <Button
              onClick={() => setOpenCuitModal(false)}
              variant="outlined"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                if (/^\d{11}$/.test(cuitGlobal)) {
                  setOpenCuitModal(false)
                  pasarFacturados() // ahora ya tenemos cuitGlobal
                } else {
                  alert("El CUIT debe tener 11 dígitos.")
                }
              }}
              variant="contained"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 500,
                bgcolor: theme.palette.secondary.main,
                "&:hover": {
                  bgcolor: alpha(theme.palette.secondary.main, 0.9),
                },
              }}
            >
              Aceptar
            </Button>
          </DialogActions>
        </Dialog>

        {openModalDevolucion === true ? (
          <ModalViewTablaDevolucion
            openModal={openModalDevolucion}
            handleCloseModal={handleCloseModal}
            selectedTransaccion={selectedTransaccion}
            selectedTransaccionEntidad={selectedTransaccionEntidad}
            transaccionItems={transaccionItems}
          />
        ) : null}
        {(facturacionLoading || facturacionDone) && (
          <LoadingOverlay
            facturacionLoading={facturacionLoading}
            done={facturacionDone}
            errors={errorList}
            progressCount={progressCount}
            progressTotal={progressTotal}
            onAccept={() => {
              setFacturacionDone(false)
              setErrorList([])
              setFacturarSeleccionados([])
              setFacturacionLoading(false)
              // refrescá tus datos
              fetchData(
                startDate,
                page,
                endDate,
                dniCliente,
                selectedUbicacion,
                minVentas,
                maxVentas,
                mostrarDeletes,
                idTransaccionSearch,
                100,
                soloFacturadas,
              )
            }}
          />
        )}
      </ThemeProvider>
    </Box>
  )
}
